import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../api';
import PoolChat from '../components/PoolChat';
import UserStatsModal from '../components/UserStatsModal';
import WeeklyRecap from '../components/WeeklyRecap';
import PaywallBanner from '../components/PaywallBanner';
import { kickoffPhrase } from '../dates';
import NotifyBanner from '../components/NotifyBanner';
import PushNotify from '../components/PushNotify';
import { ensureGames } from '../gamesStore';

// espn = ESPN CDN slug (lowercase); defaults to abbr.toLowerCase() if omitted
const TEAMS = [
  { abbr: 'ARI', name: 'Cardinals',  color: '#97233F', alt: '#FFB612' },
  { abbr: 'ATL', name: 'Falcons',    color: '#A71930', alt: '#000000' },
  { abbr: 'BAL', name: 'Ravens',     color: '#241773', alt: '#9E7C0C' },
  { abbr: 'BUF', name: 'Bills',      color: '#00338D', alt: '#C60C30' },
  { abbr: 'CAR', name: 'Panthers',   color: '#0085CA', alt: '#000000' },
  { abbr: 'CHI', name: 'Bears',      color: '#0B162A', alt: '#C83803' },
  { abbr: 'CIN', name: 'Bengals',    color: '#FB4F14', alt: '#000000' },
  { abbr: 'CLE', name: 'Browns',     color: '#311D00', alt: '#FF3C00' },
  { abbr: 'DAL', name: 'Cowboys',    color: '#003594', alt: '#869397' },
  { abbr: 'DEN', name: 'Broncos',    color: '#FB4F14', alt: '#002244' },
  { abbr: 'DET', name: 'Lions',      color: '#0076B6', alt: '#B0B7BC' },
  { abbr: 'GB',  name: 'Packers',    color: '#203731', alt: '#FFB612' },
  { abbr: 'HOU', name: 'Texans',     color: '#03202F', alt: '#A71930' },
  { abbr: 'IND', name: 'Colts',      color: '#002C5F', alt: '#A2AAAD' },
  { abbr: 'JAX', name: 'Jaguars',    color: '#006778', alt: '#9F792C', espn: 'jac' },
  { abbr: 'KC',  name: 'Chiefs',     color: '#E31837', alt: '#FFB81C' },
  { abbr: 'LAC', name: 'Chargers',   color: '#0080C6', alt: '#FFC20E' },
  { abbr: 'LAR', name: 'Rams',       color: '#003594', alt: '#FFA300' },
  { abbr: 'LV',  name: 'Raiders',    color: '#000000', alt: '#A5ACAF' },
  { abbr: 'MIA', name: 'Dolphins',   color: '#008E97', alt: '#FC4C02' },
  { abbr: 'MIN', name: 'Vikings',    color: '#4F2683', alt: '#FFC62F' },
  { abbr: 'NE',  name: 'Patriots',   color: '#002244', alt: '#C60C30' },
  { abbr: 'NO',  name: 'Saints',     color: '#101820', alt: '#D3BC8D' },
  { abbr: 'NYG', name: 'Giants',     color: '#0B2265', alt: '#A71930' },
  { abbr: 'NYJ', name: 'Jets',       color: '#125740', alt: '#000000' },
  { abbr: 'PHI', name: 'Eagles',     color: '#004C54', alt: '#A5ACAF' },
  { abbr: 'PIT', name: 'Steelers',   color: '#101820', alt: '#FFB612' },
  { abbr: 'SEA', name: 'Seahawks',   color: '#002244', alt: '#69BE28' },
  { abbr: 'SF',  name: '49ers',      color: '#AA0000', alt: '#B3995D' },
  { abbr: 'TB',  name: 'Buccaneers', color: '#D50A0A', alt: '#FF7900' },
  { abbr: 'TEN', name: 'Titans',     color: '#0C2340', alt: '#4B92DB' },
  { abbr: 'WAS', name: 'Commanders', color: '#773141', alt: '#FFB612', espn: 'wsh' },
];

function teamLogoUrl(team) {
  const slug = team.espn || team.abbr.toLowerCase();
  return `https://a.espncdn.com/i/teamlogos/nfl/500/${slug}.png`;
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function getWinPctColor(pct) {
  if (pct >= 70) return '#4caf50';
  if (pct >= 55) return '#8bc34a';
  if (pct >= 45) return '#ffa726';
  return '#ef5350';
}

function getRankEmoji(rank) {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
}

export default function HomePage({ user, pools, poolsLoaded, selectedPool, membership = null, onNavigate, onUserUpdate, dmUnread, onDmRead, openDmWith = null }) {
  const [weekData, setWeekData] = useState(null);
  const [myPicks, setMyPicks] = useState([]);
  // Every sheet this member holds — one per pool since picks became per-entry.
  // `myPicks` is only ever ONE of them, so anything phrased as "your week" has
  // to be checked against this list or it quietly describes a single pool.
  const [sheets, setSheets] = useState([]);
  const [sheetTotalGames, setSheetTotalGames] = useState(0);
  const [picksEntryId, setPicksEntryId] = useState(null);
  const [poolStandings, setPoolStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [winnerInfo, setWinnerInfo] = useState(null);
  const [winnerDismissed, setWinnerDismissed] = useState(false);
  const [chatPoolId, setChatPoolId] = useState(null);
  const [myBadges, setMyBadges] = useState(null); // { summary, perfectWeeks: [...], poolWins: [...] }
  const [poolMembers, setPoolMembers] = useState([]); // members of chatPoolId
  const [confirmNudges, setConfirmNudges] = useState([]); // pools where the commissioner hasn't confirmed me
  const [statsUserId, setStatsUserId] = useState(null);
  const [statsInitialTab, setStatsInitialTab] = useState('stats');
  // Username editor state
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMsg, setNameMsg] = useState(null);

  const openNameEdit = () => {
    setNameDraft(user.username);
    setNameMsg(null);
    setEditingName(true);
  };
  const cancelNameEdit = () => {
    setEditingName(false);
    setNameMsg(null);
  };
  const saveName = async () => {
    const v = nameDraft.trim();
    if (!v || v === user.username) { cancelNameEdit(); return; }
    setNameSaving(true);
    setNameMsg(null);
    try {
      const res = await API.put('/auth/username', { username: v });
      if (onUserUpdate) onUserUpdate({ username: res.data.username }, res.data.token);
      setEditingName(false);
      setNameMsg({ ok: true, text: `✅ Username changed to "${res.data.username}"` });
      setTimeout(() => setNameMsg(null), 4000);
    } catch (err) {
      setNameMsg({ ok: false, text: err.response?.data?.error || 'Failed to change username' });
    } finally {
      setNameSaving(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      const [gamesShared, picksRes, winnersRes, badgesRes, entriesRes] = await Promise.all([
        ensureGames(),
        API.get('/picks/mine'),
        API.get('/pools/winners/weekly').catch(() => ({ data: null })),
        API.get('/auth/me/badges').catch(() => ({ data: null })),
        API.get('/entries/mine').catch(() => ({ data: null })),
      ]);
      setWeekData({ games: gamesShared.games, week: gamesShared.week });
      setMyPicks(picksRes.data.picks || []);
      setPicksEntryId(picksRes.data.entry_id ?? null);
      setSheets(entriesRes.data?.entries || []);
      setSheetTotalGames(entriesRes.data?.totalGames || 0);
      setWinnerInfo(winnersRes.data || null);
      setMyBadges(badgesRes.data || null);

      // Load leaderboard position for each pool
      if (pools && pools.length > 0) {
        const standingResults = await Promise.all(
          pools.map(pool =>
            API.get(`/picks/leaderboard?pool_id=${pool.id}`)
              .then(r => ({ pool, data: r.data }))
              .catch(() => null)
          )
        );
        setPoolStandings(standingResults.filter(Boolean));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [pools]);

  // Dismiss is keyed on the week id — persists until a new week is finalized
  useEffect(() => {
    if (winnerInfo?.week?.id) {
      const dismissedWeek = localStorage.getItem('nfl_winner_banner_dismissed');
      setWinnerDismissed(dismissedWeek === String(winnerInfo.week.id));
    }
  }, [winnerInfo?.week?.id]);

  const dismissBanner = () => {
    if (winnerInfo?.week?.id) {
      localStorage.setItem('nfl_winner_banner_dismissed', String(winnerInfo.week.id));
    }
    setWinnerDismissed(true);
  };

  // Default the chat to the first pool once pools load (or if the selected one disappears)
  useEffect(() => {
    if (!pools || pools.length === 0) {
      setChatPoolId(null);
      return;
    }
    setChatPoolId(prev => {
      if (prev && pools.some(p => p.id === prev)) return prev;
      return pools[0].id;
    });
  }, [pools]);

  // Pools where my commissioner hasn't confirmed me yet (one cheap call)
  useEffect(() => {
    let cancelled = false;
    API.get('/confirmations')
      .then(res => { if (!cancelled) setConfirmNudges(res.data?.nudges || []); })
      .catch(() => { if (!cancelled) setConfirmNudges([]); });
    return () => { cancelled = true; };
  }, [pools]);

  // Load members for the active pool (sidebar "Pool Members" card)
  useEffect(() => {
    if (!chatPoolId) {
      setPoolMembers([]);
      return;
    }
    let cancelled = false;
    API.get(`/pools/${chatPoolId}`)
      .then(res => {
        if (cancelled) return;
        setPoolMembers(res.data?.members || []);
      })
      .catch(() => {
        if (cancelled) return;
        setPoolMembers([]);
      });
    return () => { cancelled = true; };
  }, [chatPoolId]);

  const openMemberStats = (uid, tab = 'stats') => {
    setStatsInitialTab(tab);
    setStatsUserId(uid);
  };

  // Arrived from a DM notification: open that conversation rather than dropping
  // them on the page and leaving them to find the sender in the members list.
  //
  // Keyed on `seq`, not on the sender's id. Two messages from the same person
  // carry the same id, so an id-keyed effect would fire once and then never
  // again — tap the second notification after closing the thread and nothing
  // would happen.
  const lastDmSeq = useRef(-1);
  useEffect(() => {
    const req = openDmWith;
    if (!req || !req.id || req.seq === lastDmSeq.current) return;
    lastDmSeq.current = req.seq;
    openMemberStats(req.id, 'messages');
  }, [openDmWith]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Compute my weekly stats
  const games = weekData?.games || [];
  const week = weekData?.week;
  // `regularGames` is still the non-tiebreaker list, used where the tiebreaker
  // genuinely is a separate thing (it has its own card on the picks page). Every
  // COUNT below is over all games, because the tiebreaker scores like any other
  // one now — a record of 6 out of 7 shouldn't be reported as 5 out of 6.
  const regularGames = games.filter(g => !g.is_tiebreaker);
  const allGamesCount = games.length;
  const pickedCount = myPicks.length;
  const correctCount = myPicks.filter(p => p.is_correct === 1).length;
  const wrongCount = myPicks.filter(p => p.is_correct === 0).length;
  // Pending is a property of the GAME, not of the pick. A tied game is final
  // and settled, but every pick on it keeps `is_correct === null` — nobody took
  // the winner, because there wasn't one — which is the same value an unplayed
  // game carries. Reading pending off the pick alone is what put "1 PENDING" on
  // this card under a week where every game had finished.
  //
  // A tie is a push: it counts for nobody and against nobody. It gets its own
  // tile below rather than being folded into wrong, because being told you lost
  // a game nobody could win is worse than being told nothing.
  const pushCount = myPicks.filter(p => p.is_correct === null && p.status === 'final').length;
  const pendingCount = myPicks.filter(p => p.is_correct === null && p.status !== 'final').length;
  const completedGames = games.filter(g => g.status === 'final').length;
  const liveGames = games.filter(g => g.status === 'in_progress').length;
  const unpickedCount = Math.max(0, allGamesCount - pickedCount);
  const winPct = (correctCount + wrongCount) > 0 ? Math.round((correctCount / (correctCount + wrongCount)) * 100) : null;
  // Every game done = the week is over. Used so a finished week isn't described
  // as "underway", and so we don't nag about picks that can no longer be made.
  const weekOver = allGamesCount > 0 && completedGames === allGamesCount;
  const stillPickable = games.some(g => g.status === 'scheduled');

  /* A week is underway once a game has actually kicked off, and not one minute
     before. The line under your name read "Week 1 is underway" all Saturday with
     every game still scheduled and the first kickoff four days out, because the
     only state it checked was whether the week had FINISHED. Four lines below a
     banner correctly saying kickoff is Wednesday, the same screen contradicted
     itself. */
  const weekStarted = completedGames > 0 || liveGames > 0;

  /* The first kickoff in the week, so a week that has not started can say when
     it will rather than just denying it has. */
  const firstKickoff = games
    .map(g => g.game_date_iso)
    .filter(Boolean)
    .map(iso => new Date(iso))
    .filter(d => !Number.isNaN(d.getTime()))
    .sort((a, b) => a - b)[0] || null;

  // ── Whose week is this, exactly? ───────────────────────────────────────────
  //
  // Picks belong to a seat, and a member in two pools has two seats with two
  // independent sheets. /picks/mine without an entry answers for one of them —
  // the oldest — so every number above describes a single pool. Said plainly on
  // a page headed "THIS WEEK", that reads as a claim about all of them: pick in
  // both pools and the card still shows one pool's total, which is exactly the
  // wrong moment to be reassuring.
  //
  // So when there's more than one sheet, say which pool the numbers are for and
  // show the rest, and let "all submitted" mean all of them.
  const perSheetTotal = sheetTotalGames || allGamesCount;
  const multiSheet = sheets.length > 1;
  const shownSheet = sheets.find(s => s.entry_id === picksEntryId) || null;
  const unfinishedSheets = sheets.filter(s => (s.weekPicks || 0) < perSheetTotal);
  // Complete means every sheet is full, and full is measured against every game
  // — the tiebreaker scores like any other one, so a sheet missing it is not done.
  const allSheetsDone = multiSheet
    ? sheets.length > 0 && unfinishedSheets.length === 0
    : allGamesCount > 0 && pickedCount === allGamesCount;

  // A sheet is identified by its pool — that's how members think of them. The
  // entry's own name (which is just the username, or "name B" for a second
  // entry) is only worth showing when two seats share a pool and the pool name
  // alone wouldn't tell them apart.
  const poolSeatCounts = sheets.reduce((m, s) => {
    const k = s.pool_id == null ? 'personal' : s.pool_id;
    m[k] = (m[k] || 0) + 1;
    return m;
  }, {});
  const sheetLabel = (s) => {
    const pool = s.pool_name || 'Personal';
    const k = s.pool_id == null ? 'personal' : s.pool_id;
    return poolSeatCounts[k] > 1 && s.name ? `${pool}: ${s.name}` : pool;
  };

  // Duplicate teams for seamless loop
  const scrollTeams = [...TEAMS, ...TEAMS];

  return (
    <div style={{ minHeight: '100vh', background: '#0a0e1a', overflow: 'hidden' }}>
      {/* Inject keyframes */}
      <style>{`
        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.6; }
        }
        .home-card {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 16px;
          padding: 24px;
          transition: border-color 0.2s, transform 0.2s;
        }
        .home-card:hover {
          border-color: rgba(255,255,255,0.15);
          transform: translateY(-2px);
        }
        .action-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 20px;
          border-radius: 10px;
          border: none;
          cursor: pointer;
          font-size: 15px;
          font-weight: 700;
          font-family: 'Oswald', sans-serif;
          letter-spacing: 0.5px;
          transition: all 0.15s;
          text-align: left;
          width: 100%;
        }
        .action-btn:hover { transform: translateY(-1px); filter: brightness(1.1); }
        .member-row:hover {
          background: rgba(127,179,255,0.06) !important;
          border-color: rgba(127,179,255,0.2) !important;
          transform: translateX(2px);
        }
        .member-row button:hover {
          transform: translateY(-1px);
          filter: brightness(1.2);
        }
        .pool-members-scroll::-webkit-scrollbar { width: 8px; }
        .pool-members-scroll::-webkit-scrollbar-track { background: rgba(0,0,0,0.2); border-radius: 4px; }
        .pool-members-scroll::-webkit-scrollbar-thumb {
          background: rgba(127,179,255,0.25);
          border-radius: 4px;
        }
        .pool-members-scroll::-webkit-scrollbar-thumb:hover { background: rgba(127,179,255,0.45); }
      `}</style>


      {/* ── Weekly winner banner (only once the latest week is final) ── */}
      {winnerInfo?.finalized && !winnerDismissed && winnerInfo.winners?.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(255,215,0,0.15), rgba(213,10,10,0.15))',
          borderTop: '1px solid rgba(255,215,0,0.25)',
          borderBottom: '1px solid rgba(255,215,0,0.25)',
          padding: '16px 20px',
          animation: 'fadeInUp 0.6s ease'
        }}>
          <div style={{ maxWidth: 960, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 12, color: '#FFD700', letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>
                🏆 {winnerInfo.week?.label || 'Week'} Final Results
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                {winnerInfo.winners.map(w => (
                  <div key={w.pool_id} style={{ fontSize: 15, color: '#fff', lineHeight: 1.4 }}>
                    <span style={{ color: '#7fb3ff', fontWeight: 700 }}>{w.pool_name}:</span>{' '}
                    {w.winner ? (
                      <>
                        <span style={{ fontWeight: 700 }}>
                          {w.tied
                            ? `🤝 Tied with ${(w.tiedWith || []).join(', ')}`
                            : `🥇 ${w.winner.username}`}
                        </span>
                        <span style={{ color: '#aab', fontSize: 13, marginLeft: 6 }}>
                          ({w.winner.correct} correct
                          {w.winner.tbDiff != null && !w.tied ? `, tiebreaker off by ${w.winner.tbDiff}` : ''})
                        </span>
                      </>
                    ) : (
                      <span style={{ color: '#778', fontSize: 13 }}>No eligible picks this week</span>
                    )}
                  </div>
                ))}
              </div>
              {winnerInfo.tiebreakerGame && (
                <div style={{ fontSize: 12, color: '#889', marginTop: 8 }}>
                  {/* "MNF" was a lie in any week that doesn't end on Monday —
                      preseason, Saturday weeks, a Tuesday reschedule. The
                      tiebreaker is the last game of the week, whichever that is. */}
                  Tiebreaker: {winnerInfo.tiebreakerGame.away_abbr} {winnerInfo.tiebreakerGame.away_score} to {winnerInfo.tiebreakerGame.home_score} {winnerInfo.tiebreakerGame.home_abbr} (total {winnerInfo.tiebreakerGame.total})
                </div>
              )}
            </div>
            <button
              onClick={dismissBanner}
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: '#aab',
                padding: '6px 12px',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 700
              }}
              title="Dismiss until next week"
            >✕ Dismiss</button>
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 20px' }}>

        {/* The one place that actually asks. Members land here; the toggle on the
            Weekly tab was only ever found by people already looking for it. */}
        <NotifyBanner />

        {/* Once they've said yes the banner disappears, and until now that left
            nowhere on this page to change your mind — the only switch was on
            the Weekly tab, and it couldn't switch anything off anyway. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
          <PushNotify />
        </div>


        {/* Weekly Recap — the pool the app has selected, not simply the first one.
            `pools[0]` meant somebody in three pools always saw the same pool's
            recap here however they'd switched elsewhere, and for somebody in no
            pool it was undefined, which asked for an unscoped board of strangers. */}
        {/* ── Finish your picks ──
            A BUTTON, and above everything else on the page.

            This was a line of 13px text reading "you still have 4 unpicked
            games", sitting 1110px down inside the THIS WEEK card. So the app
            named the one thing that was wrong, made it untappable, and put it
            below the fold on a phone. Measured: you had to scroll past a score
            ticker, a greeting, a badge row and five stat tiles to reach the
            sentence, and past two more cards to reach the button that fixed it.

            Now it is the first thing on the page and it goes straight to the
            picks. It is also the only red button on the screen while picks are
            open, so the one path that matters is the one that stands out.

            Above the membership banner on purpose. Rewriting that banner for
            launch week took it from 334px to 308, which is not the difference
            between above and below the fold, and it still sat on top. Picking is
            the thing this app is for and it is free either way; the notice about
            paying is the second thing somebody needs, not the first, and it is
            immediately underneath where nobody can miss it.

            It renders only while there is still something to pick. A finished
            week telling you to go and make picks you cannot make is worse than
            saying nothing. */}
        {week && !allSheetsDone && !week.picks_locked && stillPickable && (
          <button
            onClick={() => onNavigate('picks')}
            style={{
              width: '100%', minHeight: 44, display: 'flex', alignItems: 'center', gap: 10,
              background: 'linear-gradient(135deg, rgba(213,10,10,0.22), rgba(139,0,0,0.22))',
              border: '1px solid rgba(213,10,10,0.55)', borderRadius: 10,
              padding: '13px 16px', marginBottom: 20,
              fontFamily: 'inherit', fontSize: 14.5, color: '#ff9b9b', fontWeight: 700,
              textAlign: 'left', cursor: 'pointer',
              boxShadow: '0 0 0 3px rgba(213,10,10,0.10)',
              animation: 'fadeInUp 0.5s ease'
            }}
          >
            <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              {multiSheet
                ? `${unfinishedSheets.length} of your ${sheets.length} sheets ${unfinishedSheets.length === 1 ? 'is' : 'are'} incomplete`
                : `You still have ${unpickedCount} unpicked game${unpickedCount > 1 ? 's' : ''}`}
            </span>
            {/* Says what pressing it does. An alert that is secretly a button is
                worse than either one on its own. */}
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.5, color: '#ffd0d0', flexShrink: 0 }}>
              FINISH →
            </span>
          </button>
        )}

        <PaywallBanner membership={membership} onNavigate={onNavigate} />

        <WeeklyRecap user={user} selectedPool={selectedPool} />

        {/* Confirmation nudge — commissioner hasn't ticked you off yet */}
        {confirmNudges.length > 0 && (
          <div style={{
            marginBottom: 20,
            background: 'linear-gradient(135deg, rgba(255,179,71,0.12), rgba(1,51,105,0.10))',
            border: '1px solid rgba(255,179,71,0.32)',
            borderRadius: 12,
            padding: '14px 18px',
            animation: 'fadeInUp 0.5s ease'
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontFamily: "'Oswald', sans-serif",
              color: '#ffb347', fontSize: 13, fontWeight: 700,
              letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8
            }}>
              ⏳ Not confirmed yet
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {confirmNudges.map(n => (
                <div key={n.pool_id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  flexWrap: 'wrap', gap: 8, padding: '8px 12px',
                  background: 'rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,179,71,0.16)',
                  borderRadius: 8
                }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ color: '#dde', fontSize: 13, fontWeight: 700 }}>{n.pool_name}</div>
                    <div style={{ color: '#aab', fontSize: 12, marginTop: 2 }}>
                      {[
                        n.needsSeason ? 'Season not confirmed' : null,
                        n.needsWeek ? `${n.week_label || 'This week'} not confirmed` : null
                      ].filter(Boolean).join(' · ')}
                      {'. Check with your commissioner.'}
                    </div>
                  </div>
                  <button
                    onClick={() => onNavigate('pools')}
                    style={{
                      padding: '7px 14px', borderRadius: 7, border: 'none',
                      background: 'linear-gradient(135deg, #013369, #0a5299)',
                      color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      flexShrink: 0
                    }}
                  >Go to Pool →</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Greeting */}
        <div style={{ marginBottom: 32, animation: 'fadeInUp 0.5s ease' }}>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 13, color: '#7fb3ff', letterSpacing: 2, marginBottom: 6, textTransform: 'uppercase' }}>
            {getGreeting()}
          </div>
          {editingName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <input
                autoFocus
                value={nameDraft}
                onChange={e => setNameDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') saveName();
                  if (e.key === 'Escape') cancelNameEdit();
                }}
                maxLength={30}
                style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontSize: 32,
                  fontWeight: 700,
                  color: '#fff',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(127,179,255,0.4)',
                  borderRadius: 8,
                  padding: '4px 12px',
                  outline: 'none',
                  minWidth: 220
                }}
              />
              <button
                onClick={saveName}
                disabled={nameSaving || !nameDraft.trim()}
                style={{ padding: '8px 16px', borderRadius: 7, border: 'none', background: 'linear-gradient(135deg,#013369,#0a5299)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13, opacity: nameSaving || !nameDraft.trim() ? 0.6 : 1 }}
              >{nameSaving ? 'Saving…' : 'Save'}</button>
              <button
                onClick={cancelNameEdit}
                disabled={nameSaving}
                style={{ padding: '8px 14px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)', color: '#aab', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
              >Cancel</button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 36, fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>
                {user.username} 🏈
              </div>
              <button
                onClick={openNameEdit}
                title="Change your username"
                style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)', color: '#889', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
              >✏️ Edit name</button>
            </div>
          )}
          {nameMsg && (
            <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: nameMsg.ok ? '#4caf50' : '#ff8888' }}>
              {nameMsg.text}
            </div>
          )}
          {/* Season badges and preseason badges are counted separately now, so the
              gate has to look at all four. Checking only the season pair hid a
              profile's entire badge row the moment the season reset to 0-0,
              which is exactly the thing that was meant to be kept. */}
          {myBadges?.summary && (myBadges.summary.perfectWeeks > 0 || myBadges.summary.poolWins > 0
            || myBadges.summary.preseasonPerfectWeeks > 0 || myBadges.summary.preseasonPoolWins > 0) && (
            <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
              {myBadges.summary.perfectWeeks > 0 && (
                <div
                  title={
                    myBadges.perfectWeeks?.length
                      ? 'Perfect weeks: ' + myBadges.perfectWeeks.map(w => w.label).join(', ')
                      : `${myBadges.summary.perfectWeeks} perfect week${myBadges.summary.perfectWeeks > 1 ? 's' : ''}`
                  }
                  style={{
                    padding: '5px 12px',
                    borderRadius: 20,
                    background: 'linear-gradient(135deg, rgba(255,215,0,0.18), rgba(255,215,0,0.08))',
                    border: '1px solid rgba(255,215,0,0.35)',
                    color: '#FFD700',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    cursor: 'help'
                  }}
                >⭐ {myBadges.summary.perfectWeeks}× Perfect Week</div>
              )}
              {myBadges.summary.poolWins > 0 && (
                <div
                  title={
                    myBadges.poolWins?.length
                      ? myBadges.poolWins.map(p => `${p.pool_name}: ${p.label}${p.tied ? ' (tied)' : ''}`).join('\n')
                      : `${myBadges.summary.poolWins} pool win${myBadges.summary.poolWins > 1 ? 's' : ''}`
                  }
                  style={{
                    padding: '5px 12px',
                    borderRadius: 20,
                    background: 'linear-gradient(135deg, rgba(213,10,10,0.18), rgba(213,10,10,0.08))',
                    border: '1px solid rgba(213,10,10,0.35)',
                    color: '#ff8b8b',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    cursor: 'help'
                  }}
                >🏆 {myBadges.summary.poolWins}× Weekly Pool Win</div>
              )}
              {/* Earned, kept, and labelled. Muted rather than gold or red,
                  because a preseason badge should read as the smaller thing it
                  is without being hidden. */}
              {myBadges.summary.preseasonPerfectWeeks > 0 && (
                <div
                  title={'Preseason perfect weeks: ' +
                    (myBadges.perfectWeeks || []).filter(w => w.preseason).map(w => w.label).join(', ')}
                  style={{
                    padding: '5px 12px', borderRadius: 20,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    color: '#99a', fontSize: 12, fontWeight: 700,
                    letterSpacing: 0.5, cursor: 'help'
                  }}
                >⭐ {myBadges.summary.preseasonPerfectWeeks}× Perfect Week (preseason)</div>
              )}
              {myBadges.summary.preseasonPoolWins > 0 && (
                <div
                  title={(myBadges.poolWins || []).filter(p => p.preseason)
                    .map(p => `${p.pool_name}: ${p.label}${p.tied ? ' (tied)' : ''}`).join('\n')}
                  style={{
                    padding: '5px 12px', borderRadius: 20,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    color: '#99a', fontSize: 12, fontWeight: 700,
                    letterSpacing: 0.5, cursor: 'help'
                  }}
                >🏆 {myBadges.summary.preseasonPoolWins}× Pool Win (preseason)</div>
              )}
            </div>
          )}
          {week && (
            <div style={{ color: '#556', fontSize: 15, marginTop: 6 }}>
              {week.label}{' '}
              {weekOver ? 'is in the books'
                : weekStarted ? 'is underway'
                : firstKickoff ? `starts ${kickoffPhrase(firstKickoff)}`
                : 'has not kicked off yet'}
              {liveGames > 0 && <span style={{ color: '#ffa726', marginLeft: 8, animation: 'pulse 1.5s ease infinite' }}>● {liveGames} game{liveGames > 1 ? 's' : ''} LIVE</span>}
            </div>
          )}
        </div>

        {/* ── Top stats row ── */}
        {/* These five all come from one sheet. With two pools in play that is a
            claim worth qualifying out loud — the per-pool breakdown is in THIS
            WEEK below. */}
        {week && multiSheet && (
          <div style={{ fontSize: 12, color: '#667', marginBottom: 8, marginTop: -18 }}>
            Your record in <strong style={{ color: '#99a' }}>{shownSheet?.pool_name || shownSheet?.name || 'your first pool'}</strong>
            {'. Every sheet is listed below.'}
          </div>
        )}
        {week && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 28, animation: 'fadeInUp 0.55s ease' }}>
            {[
              { label: 'Picked',    value: `${pickedCount}/${allGamesCount}`, color: '#7fb3ff', emoji: '📋' },
              { label: 'Correct',   value: correctCount,  color: '#4caf50', emoji: '✅' },
              { label: 'Wrong',     value: wrongCount,    color: '#ef5350', emoji: '❌' },
              // Only when there is one — a permanent "0 TIED" tile would be
              // noise in every week of a normal season.
              ...(pushCount > 0 ? [{ label: 'Tied', value: pushCount, color: '#8b95a8', emoji: '🤝' }] : []),
              { label: 'Pending',   value: pendingCount,  color: '#ffa726', emoji: '⏳' },
              ...(winPct !== null ? [{ label: 'Win %', value: `${winPct}%`, color: getWinPctColor(winPct), emoji: '📈' }] : []),
            ].map(stat => (
              <div key={stat.label} className="home-card" style={{ textAlign: 'center', padding: '18px 12px' }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>{stat.emoji}</div>
                <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 26, fontWeight: 700, color: stat.color }}>{stat.value}</div>
                <div style={{ fontSize: 12, color: '#556', marginTop: 2, fontWeight: 600, letterSpacing: 0.5 }}>{stat.label.toUpperCase()}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Main grid: picks progress + pool standings ── */}
        {/*
          Two columns when there's room for two, one when there isn't.

          This was a hard `1fr 1fr` whenever the member was in any pool, with no
          mobile case at all — so on a phone THIS WEEK and YOUR POOL RANKS were
          each crushed into about 180px and the ranks card lost its right-hand
          side. `auto-fit` with a floor does the same job on a laptop and stops
          doing it when the screen can't take it, without a breakpoint to keep
          in step with the content.
        */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: pools?.length > 0
            ? 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))'
            : '1fr',
          gap: 20, marginBottom: 28, animation: 'fadeInUp 0.6s ease'
        }}>

          {/* Picks progress card */}
          <div className="home-card">
            <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 16, fontWeight: 700, color: '#7fb3ff', letterSpacing: 1, marginBottom: multiSheet ? 4 : 16 }}>
              📋 THIS WEEK
            </div>
            {/* Name the pool these numbers belong to, rather than letting them
                pass for all of them. */}
            {multiSheet && (
              <div style={{ fontSize: 12, color: '#667', marginBottom: 16 }}>
                Showing <strong style={{ color: '#99a' }}>{shownSheet?.pool_name || shownSheet?.name || 'your first pool'}</strong>
                {'. You have '}{sheets.length}{' sheets this week.'}
              </div>
            )}

            {!week ? (
              <div style={{ color: '#445', fontSize: 14 }}>No active week loaded yet.</div>
            ) : (
              <>
                {/* Progress bar */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: '#889', fontWeight: 600 }}>PICKS SUBMITTED</span>
                    <span style={{ fontSize: 12, color: '#7fb3ff', fontWeight: 700 }}>{pickedCount} / {allGamesCount}</span>
                  </div>
                  <div style={{ height: 8, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${allGamesCount ? (pickedCount / allGamesCount) * 100 : 0}%`,
                      background: 'linear-gradient(90deg, #013369, #0a5299)',
                      borderRadius: 4,
                      transition: 'width 0.4s ease'
                    }} />
                  </div>
                </div>

                {/* Games progress */}
                {/* Both halves count the same set of games. `completedGames` is
                    over every game in the week, so pairing it with the
                    tiebreaker-excluded list read "7 / 6" once the week finished
                    — and drove the bar past 100%. */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: '#889', fontWeight: 600 }}>GAMES COMPLETED</span>
                    <span style={{ fontSize: 12, color: '#4caf50', fontWeight: 700 }}>{completedGames} / {allGamesCount}</span>
                  </div>
                  <div style={{ height: 8, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${allGamesCount ? (completedGames / allGamesCount) * 100 : 0}%`,
                      background: 'linear-gradient(90deg, #1b5e20, #4caf50)',
                      borderRadius: 4,
                      transition: 'width 0.4s ease'
                    }} />
                  </div>
                </div>

                {/* W/L mini display */}
                {(correctCount + wrongCount) > 0 && (
                  <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                    <div style={{ flex: 1, background: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.25)', borderRadius: 8, padding: '10px', textAlign: 'center' }}>
                      <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 24, fontWeight: 700, color: '#4caf50' }}>{correctCount}</div>
                      <div style={{ fontSize: 11, color: '#556' }}>CORRECT</div>
                    </div>
                    <div style={{ flex: 1, background: 'rgba(239,83,80,0.1)', border: '1px solid rgba(239,83,80,0.25)', borderRadius: 8, padding: '10px', textAlign: 'center' }}>
                      <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 24, fontWeight: 700, color: '#ef5350' }}>{wrongCount}</div>
                      <div style={{ fontSize: 11, color: '#556' }}>WRONG</div>
                    </div>
                    {winPct !== null && (
                      <div style={{ flex: 1, background: 'rgba(127,179,255,0.08)', border: '1px solid rgba(127,179,255,0.2)', borderRadius: 8, padding: '10px', textAlign: 'center' }}>
                        <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 24, fontWeight: 700, color: getWinPctColor(winPct) }}>{winPct}%</div>
                        <div style={{ fontSize: 11, color: '#556' }}>WIN %</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Every sheet, so "am I done?" is answerable without visiting
                    each pool in turn. */}
                {multiSheet && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: '#889', fontWeight: 600, marginBottom: 8 }}>YOUR SHEETS</div>
                    {sheets.map(s => {
                      const done = (s.weekPicks || 0) >= perSheetTotal;
                      return (
                        <div key={s.entry_id} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '8px 12px', marginBottom: 6, borderRadius: 8,
                          background: done ? 'rgba(76,175,80,0.08)' : 'rgba(213,10,10,0.08)',
                          border: `1px solid ${done ? 'rgba(76,175,80,0.22)' : 'rgba(213,10,10,0.22)'}`
                        }}>
                          <span style={{ fontSize: 13, color: '#dde', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {done ? '✅' : '⚠️'} {sheetLabel(s)}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: done ? '#4caf50' : '#ff8888', flexShrink: 0, marginLeft: 10 }}>
                            {s.weekPicks || 0} / {perSheetTotal}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!allSheetsDone && weekOver && (
                  <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#889', fontWeight: 600 }}>
                    {multiSheet
                      ? `You sat this one out in ${unfinishedSheets.length} pool${unfinishedSheets.length > 1 ? 's' : ''}.`
                      : `You sat this one out. ${unpickedCount} game${unpickedCount > 1 ? 's' : ''} went unpicked.`}
                  </div>
                )}
                {allSheetsDone && (
                  <div style={{ background: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.25)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#4caf50', fontWeight: 600 }}>
                    ✅ All picks submitted{multiSheet ? ' in every pool' : ''}. Good luck!
                  </div>
                )}
              </>
            )}
          </div>

          {/* Pool standings card */}
          {pools?.length > 0 && (
            <div className="home-card">
              <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 16, fontWeight: 700, color: '#7fb3ff', letterSpacing: 1, marginBottom: 16 }}>
                🏆 YOUR POOL RANKS
              </div>
              {poolStandings.length === 0 ? (
                <div style={{ color: '#445', fontSize: 14 }}>Standings will appear once picks are in.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {poolStandings.map(({ pool, data }) => {
                    const myEntry = data.leaderboard?.find(e => e.username === user.username);
                    if (!myEntry) return null;
                    return (
                      <div
                        key={pool.id}
                        onClick={() => onNavigate('leaderboard')}
                        style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px 14px', transition: 'border-color 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(127,179,255,0.3)'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#dde', marginBottom: 2 }}>{pool.name}</div>
                            <div style={{ fontSize: 12, color: '#556' }}>{pool.member_count} members</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 26, fontWeight: 700, color: myEntry.rank <= 3 ? ['#FFD700','#C0C0C0','#CD7F32'][myEntry.rank-1] : '#7fb3ff' }}>
                              {getRankEmoji(myEntry.rank)}
                            </div>
                            <div style={{ fontSize: 12, color: '#556' }}>{myEntry.correct}W {myEntry.wrong}L</div>
                          </div>
                        </div>
                        {/* Mini rank bar */}
                        <div style={{ marginTop: 10, height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${data.leaderboard?.length > 1 ? ((data.leaderboard.length - myEntry.rank + 1) / data.leaderboard.length) * 100 : 50}%`,
                            background: myEntry.rank === 1 ? 'linear-gradient(90deg, #D50A0A, #ff6b6b)' : 'linear-gradient(90deg, #013369, #7fb3ff)',
                            borderRadius: 2
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Quick actions ── */}
        <div style={{ animation: 'fadeInUp 0.65s ease' }}>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 13, color: '#556', letterSpacing: 2, marginBottom: 12, textTransform: 'uppercase' }}>
            Quick Actions
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <button className="action-btn" style={{ background: 'linear-gradient(135deg, #D50A0A 0%, #8b0000 100%)', color: '#fff', boxShadow: '0 4px 15px rgba(213,10,10,0.3)' }} onClick={() => onNavigate('picks')}>
              <span style={{ fontSize: 22 }}>🏈</span>
              <div>
                <div>Make Picks</div>
                {unpickedCount > 0 && <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 400 }}>{unpickedCount} remaining</div>}
              </div>
            </button>
            <button className="action-btn" style={{ background: 'linear-gradient(135deg, #013369 0%, #0a5299 100%)', color: '#fff', boxShadow: '0 4px 15px rgba(1,51,105,0.4)' }} onClick={() => onNavigate('leaderboard')}>
              <span style={{ fontSize: 22 }}>📋</span>
              <div>
                <div>Weekly Board</div>
                <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 400 }}>See this week's ranks</div>
              </div>
            </button>
            <button className="action-btn" style={{ background: 'linear-gradient(135deg, #1a2a1a 0%, #2d4a2d 100%)', color: '#fff', border: '1px solid rgba(76,175,80,0.25)' }} onClick={() => onNavigate('season')}>
              <span style={{ fontSize: 22 }}>📊</span>
              <div>
                <div>Season Stats</div>
                <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 400 }}>Full standings</div>
              </div>
            </button>
            <button className="action-btn" style={{ background: 'rgba(255,255,255,0.04)', color: '#aab', border: '1px solid rgba(255,255,255,0.08)' }} onClick={() => onNavigate('pools')}>
              <span style={{ fontSize: 22 }}>🏆</span>
              <div>
                <div>My Pools</div>
                <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 400 }}>{pools?.length || 0} pool{pools?.length !== 1 ? 's' : ''}</div>
              </div>
            </button>
          </div>
        </div>

        {/* ── Pool members (sidebar card) ── */}
        {pools?.length > 0 && chatPoolId && poolMembers.length > 0 && (
          <div style={{ marginTop: 28, animation: 'fadeInUp 0.7s ease' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 12, flexWrap: 'wrap', gap: 10
            }}>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 13, color: '#556', letterSpacing: 2, textTransform: 'uppercase' }}>
                Pool Members
              </div>
              <div style={{ fontSize: 11, color: '#445', fontWeight: 600 }}>
                {poolMembers.length} total{poolMembers.length > 4 ? ' · scroll for more' : ''}
              </div>
            </div>
            <div className="home-card" style={{ padding: 0, overflow: 'hidden' }}>
              {/* Pool-name banner */}
              <div style={{
                padding: '12px 16px',
                background: 'linear-gradient(135deg, rgba(1,51,105,0.35), rgba(10,82,153,0.15))',
                borderBottom: '1px solid rgba(127,179,255,0.15)',
                display: 'flex', alignItems: 'center', gap: 10
              }}>
                <span style={{ fontSize: 16 }}>🏟️</span>
                <span style={{
                  fontFamily: "'Oswald', sans-serif", fontSize: 15,
                  color: '#7fb3ff', fontWeight: 700, letterSpacing: 0.5
                }}>
                  {pools.find(p => p.id === chatPoolId)?.name || 'Pool'}
                </span>
              </div>
              {/* Scrollable member list — ~4 rows at a time */}
              <div
                className="pool-members-scroll"
                style={{
                  maxHeight: 272, // ~4 rows of 64px + a little peek
                  overflowY: 'auto',
                  padding: 10,
                  display: 'flex', flexDirection: 'column', gap: 8
                }}
              >
                {poolMembers.map((m, idx) => {
                  const memberId = m.user_id || m.id;
                  const isMe = memberId === user.id;
                  const initial = (m.username || '?').charAt(0).toUpperCase();
                  const unread = dmUnread?.bySender?.[memberId] || 0;
                  // Deterministic avatar color from username
                  const palette = [
                    'linear-gradient(135deg, #013369, #0a5299)',
                    'linear-gradient(135deg, #3f2b96, #a8c0ff)',
                    'linear-gradient(135deg, #0d4f3f, #1f9d7a)',
                    'linear-gradient(135deg, #662d8c, #ed1e79)',
                    'linear-gradient(135deg, #c471f5, #fa71cd)',
                    'linear-gradient(135deg, #0b8793, #360033)',
                    'linear-gradient(135deg, #f7971e, #ffd200)'
                  ];
                  const bgIdx = (m.username || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % palette.length;
                  const avatarBg = isMe
                    ? 'linear-gradient(135deg, #D50A0A, #a00808)'
                    : palette[bgIdx];
                  return (
                    <div
                      key={memberId}
                      className="member-row"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 12px',
                        background: isMe
                          ? 'linear-gradient(135deg, rgba(213,10,10,0.1), rgba(213,10,10,0.02))'
                          : unread > 0
                            ? 'linear-gradient(135deg, rgba(255,140,0,0.08), rgba(255,140,0,0.02))'
                            : 'rgba(255,255,255,0.03)',
                        border: isMe
                          ? '1px solid rgba(213,10,10,0.25)'
                          : unread > 0
                            ? '1px solid rgba(255,140,0,0.35)'
                            : '1px solid rgba(255,255,255,0.06)',
                        borderRadius: 10,
                        transition: 'all 0.15s',
                        animation: `fadeInUp 0.3s ease ${idx * 0.03}s both`
                      }}
                    >
                      {/* Avatar */}
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: avatarBg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: "'Oswald', sans-serif", fontSize: 17, fontWeight: 700,
                        color: '#fff', flexShrink: 0,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        border: '2px solid rgba(255,255,255,0.08)'
                      }}>
                        {initial}
                      </div>
                      {/* Name + meta */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 14, color: '#dde', fontWeight: 700,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          display: 'flex', alignItems: 'center', gap: 6
                        }}>
                          {m.username}
                          {isMe && (
                            <span style={{
                              fontSize: 10, padding: '2px 8px', borderRadius: 10,
                              background: 'rgba(213,10,10,0.2)',
                              border: '1px solid rgba(213,10,10,0.3)',
                              color: '#ff8888', fontWeight: 700, letterSpacing: 0.5
                            }}>YOU</span>
                          )}
                          {m.is_commissioner === 1 && !isMe && (
                            <span style={{
                              fontSize: 10, padding: '2px 8px', borderRadius: 10,
                              background: 'rgba(255,215,0,0.12)',
                              border: '1px solid rgba(255,215,0,0.3)',
                              color: '#FFD700', fontWeight: 700, letterSpacing: 0.5
                            }}>COMMISH</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: '#556', marginTop: 2 }}>
                          {isMe ? 'That\'s you' : m.is_commissioner === 1 ? 'Pool commissioner' : 'Pool member'}
                        </div>
                      </div>
                      {/* Actions — only for others */}
                      {!isMe && (
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button
                            onClick={() => openMemberStats(memberId, 'stats')}
                            title={`View ${m.username}'s stats`}
                            aria-label="View stats"
                            style={{
                              width: 36, height: 36,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: 'rgba(127,179,255,0.12)',
                              border: '1px solid rgba(127,179,255,0.3)',
                              color: '#7fb3ff', borderRadius: 8,
                              fontSize: 15, cursor: 'pointer', transition: 'all 0.15s'
                            }}
                          >📊</button>
                          <button
                            onClick={() => openMemberStats(memberId, 'messages')}
                            title={unread > 0 ? `${unread} new message${unread === 1 ? '' : 's'} from ${m.username}` : `Message ${m.username}`}
                            aria-label="Send direct message"
                            style={{
                              width: 36, height: 36,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: unread > 0
                                ? 'linear-gradient(135deg, #D50A0A, #a00808)'
                                : 'rgba(213,10,10,0.15)',
                              border: unread > 0
                                ? '1px solid rgba(213,10,10,0.8)'
                                : '1px solid rgba(213,10,10,0.35)',
                              color: unread > 0 ? '#fff' : '#ff8888',
                              borderRadius: 8,
                              fontSize: 15, cursor: 'pointer', transition: 'all 0.15s',
                              position: 'relative',
                              boxShadow: unread > 0 ? '0 0 12px rgba(213,10,10,0.5)' : 'none'
                            }}
                          >
                            💬
                            {unread > 0 && (
                              <span style={{
                                position: 'absolute',
                                top: -6,
                                right: -6,
                                minWidth: 18,
                                height: 18,
                                borderRadius: 9,
                                background: '#ffae1a',
                                color: '#1a0d00',
                                fontSize: 10,
                                fontWeight: 800,
                                lineHeight: '18px',
                                padding: '0 5px',
                                border: '2px solid #0a0e1a',
                                boxShadow: '0 0 6px rgba(255,174,26,0.6)'
                              }}>{unread > 99 ? '99+' : unread}</span>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Pool chat ── */}
        {pools?.length > 0 && chatPoolId && (
          <div style={{ marginTop: 28, animation: 'fadeInUp 0.7s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 13, color: '#556', letterSpacing: 2, textTransform: 'uppercase' }}>
                Trash Talk
              </div>
              {pools.length > 1 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {pools.map(p => {
                    const active = p.id === chatPoolId;
                    return (
                      <button
                        key={p.id}
                        onClick={() => setChatPoolId(p.id)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 20,
                          border: active ? '1px solid rgba(127,179,255,0.5)' : '1px solid rgba(255,255,255,0.1)',
                          background: active ? 'rgba(127,179,255,0.15)' : 'rgba(255,255,255,0.04)',
                          color: active ? '#7fb3ff' : '#889',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 700,
                          transition: 'all 0.15s'
                        }}
                      >
                        {p.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="home-card" style={{ padding: 14 }}>
              {pools.length === 1 && (
                <div style={{ fontSize: 13, color: '#889', marginBottom: 10 }}>
                  <span style={{ color: '#7fb3ff', fontWeight: 700 }}>{pools[0].name}</span>
                </div>
              )}
              <PoolChat poolId={chatPoolId} user={user} />
            </div>
          </div>
        )}

        {/*
          The nudge for somebody in no pool. It used to sell the chat — "Join a
          pool to start chatting with the group" — which is the least of what a
          pool is for and reads as an optional social extra. Meanwhile this page
          pushes "Make Picks" as the headline action, so the one thing a new
          arrival most needed to know, that picks score in a pool and they have
          none, was the one thing nothing on screen said.
        */}
        {poolsLoaded && (!pools || pools.length === 0) && (
          <div style={{ marginTop: 28, animation: 'fadeInUp 0.7s ease' }}>
            <div className="home-card" style={{ textAlign: 'center', padding: '24px 16px' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🏆</div>
              <div style={{ color: '#dde', fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
                Your picks aren't scoring yet
              </div>
              <div style={{ color: '#889', fontSize: 14, lineHeight: 1.6 }}>
                Picks count inside a pool. Start one and share the code, or join with
                a code someone sent you. Everything you've picked so far comes with you.
              </div>
              <button
                onClick={() => onNavigate('pools')}
                style={{ marginTop: 14, padding: '10px 22px', borderRadius: 7, border: 'none', background: 'linear-gradient(135deg,#D50A0A,#a00808)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              >Create or Join a Pool</button>
            </div>
          </div>
        )}
      </div>

      <div style={{ height: 40 }} />

      {statsUserId && (
        <UserStatsModal
          userId={statsUserId}
          me={user}
          initialTab={statsInitialTab}
          onClose={() => {
            setStatsUserId(null);
            // Refresh unread counts immediately — the backend auto-marked this
            setStatsUserId(null);
            // Refresh unread counts immediately
            if (onDmRead) onDmRead();
          }}
        />
      )}
    </div>
  );
}
